import { setupHref, wizardKeyForServerStep } from '@/features/setup/setup-steps';

import { helpHref } from './deep-link-parse';
import type { TourKey } from './tours/types';

/**
 * Where a setup step's link leads — ONE mapping for every surface that sends
 * someone to a step: the dashboard card's and the hub's «ادامهٔ راه‌اندازی»,
 * the checklist's «انجامش بده», the settings nudge, the `SetupStepBar`'s
 * «مرحلهٔ بعد» and the core tour's finish (through `useSetupProgress().nextPath`).
 * It is the step's screen with the tour that teaches it
 * (`?help=tour:<key>[.<step>]`).
 *
 * The server hands out a `panelPath` per step
 * (`clinic-onboarding.service.ts`). A bare link to it left a new owner on the
 * screen with, at most, the quiet auto-offer — and only if the shared
 * proactive budget allowed one. The link now starts the matching tour; the
 * click is the owner's own, so the deep link spends no proactive budget.
 *
 * `path` is the step's CANONICAL location, not always its `panelPath`: the
 * short paths (`/settings/schedule`, `/inventory/receive`) are `<Navigate>`
 * stubs that drop the query string, and `help` with it. The onboarding
 * routing test (`app/onboarding-panelpath.test.tsx`) pins those redirects.
 *
 * The inventory step goes to the stock page with the tour on «ثبت ورود کالا»
 * rather than straight into the receive drawer (`/inventory/receive`): the
 * drawer is a modal, and a tour does not point at anything under one.
 *
 * Tours still being written (`staff`, `procedures`, `working-hours`) are
 * linked already; the deep-link handler ignores a tour with no steps, so the
 * link behaves as the bare path until the tour lands.
 */
export interface SetupStepTour {
  /**
   * The `panelPath` the server sends for this step today. The mapping applies
   * only while it still does: if the server moves a step (a plan-aware
   * inventory step pointing at the subscription, say), the server's path
   * wins and the link goes there bare, rather than to a tour of a screen the
   * step no longer means.
   */
  from: string;
  /** The canonical location the tour runs on (a path, plus `?tab=` when a tab). */
  path: string;
  tour: TourKey;
  step?: string;
}

/**
 * Only the steps the setup wizard does not cover. Profile, staff, procedures,
 * schedule and inventory go to their wizard screen (`/setup?step=…`), which
 * asks just what the step needs instead of opening the whole feature page.
 */
export const SETUP_STEP_TOURS: Readonly<Record<string, SetupStepTour>> = Object.freeze({
  appointment: { from: '/calendar', path: '/calendar', tour: 'calendar' },
});

/**
 * The href for a setup step: the wizard's screen for the five steps it covers
 * (`/setup?step=…` — profile, doctors, services, hours, stock), else its tour
 * deep link, else its `panelPath` as-is. The wizard asks only what each step
 * needs on one screen, where the feature page asks for everything it can hold.
 */
export function setupStepHref(step: { key: string; panelPath: string }): string {
  const screen = wizardKeyForServerStep(step.key, step.panelPath);
  if (screen) return setupHref(screen);
  const mapped = Object.prototype.hasOwnProperty.call(SETUP_STEP_TOURS, step.key)
    ? SETUP_STEP_TOURS[step.key]
    : undefined;
  if (!mapped || mapped.from !== step.panelPath) return step.panelPath;
  return helpHref(
    mapped.path,
    mapped.step ? { tour: mapped.tour, step: mapped.step } : { tour: mapped.tour },
  );
}
